/**
 * JavaScript XGBoost Predictor
 *
 * Evaluates XGBoost models exported in JSON format directly in the browser.
 * No backend calls needed - pure JavaScript tree traversal.
 */

import {
  isValidDetection,
  getPrimaryDetections
} from './pickleballInterpolation.js';

import {
  StreamingDetectionPredictor,
  getDetectionAtFrame,
  buildPredictionsForRange,
  DEFAULT_MAX_ANCESTOR_DEPTH
} from './DetectionPredictor.js';

export class XGBoostPredictor {
  constructor() {
    this.trees = [];
    this.baseScore = 0.5; // Default base score for binary classification
    this.loaded = false;
  }

  /**
   * Load XGBoost model from JSON
   * @param {string} modelUrl - URL to fetch the model JSON
   */
  async load(modelUrl) {
    console.log(`XGBoostPredictor: Loading model from ${modelUrl}`);
    try {
      const response = await fetch(modelUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const modelJson = await response.json();
      console.log(`XGBoostPredictor: Model JSON loaded, parsing...`);

      // Parse XGBoost JSON format
      const learner = modelJson.learner;
      if (!learner) {
        throw new Error('Invalid model format: missing learner');
      }

      const gbtree = learner.gradient_booster?.model;
      if (!gbtree) {
        throw new Error('Invalid model format: missing gradient_booster.model');
      }

      // Get base score from learner params
      // Handle both old format ('0.5') and new format ('[5E-1]')
      if (learner.learner_model_param?.base_score) {
        let baseScoreStr = learner.learner_model_param.base_score;
        // New XGBoost format wraps base_score in brackets: '[5E-1]'
        if (typeof baseScoreStr === 'string' && baseScoreStr.startsWith('[')) {
          baseScoreStr = baseScoreStr.slice(1, -1); // Remove brackets
        }
        this.baseScore = parseFloat(baseScoreStr);
      }

      // Extract trees
      if (!gbtree.trees || gbtree.trees.length === 0) {
        throw new Error('Invalid model format: no trees found');
      }

      this.trees = gbtree.trees.map(tree => ({
        splitIndices: tree.split_indices,
        splitConditions: tree.split_conditions,
        leftChildren: tree.left_children,
        rightChildren: tree.right_children,
        baseWeights: tree.base_weights,
        defaultLeft: tree.default_left
      }));

      this.loaded = true;
      console.log(`XGBoostPredictor: Model loaded successfully with ${this.trees.length} trees`);
      return true;
    } catch (error) {
      console.error('XGBoostPredictor: Failed to load model:', error);
      this.loadError = error.message;
      return false;
    }
  }

  /**
   * Evaluate a single decision tree
   * @param {Object} tree - Tree structure
   * @param {Array} features - Feature array
   * @returns {number} Leaf value
   */
  evaluateTree(tree, features) {
    let nodeIdx = 0;

    while (true) {
      const leftChild = tree.leftChildren[nodeIdx];
      const rightChild = tree.rightChildren[nodeIdx];

      // Leaf node (no children)
      if (leftChild === -1) {
        return tree.baseWeights[nodeIdx];
      }

      const featureIdx = tree.splitIndices[nodeIdx];
      const threshold = tree.splitConditions[nodeIdx];
      const featureValue = features[featureIdx];

      // Handle truly missing values (undefined/null only)
      // Note: -1 is NOT treated as missing - it's a real numeric value that gets compared normally
      // The model was trained with -1 as a sentinel, so it learned patterns from -1 comparisons
      if (featureValue === undefined || featureValue === null || Number.isNaN(featureValue)) {
        // Use default direction for missing values
        nodeIdx = tree.defaultLeft[nodeIdx] ? leftChild : rightChild;
      } else if (featureValue < threshold) {
        nodeIdx = leftChild;
      } else {
        nodeIdx = rightChild;
      }
    }
  }

  /**
   * Predict probability for binary classification
   * @param {Array} features - Feature array (60 features for temporal window)
   * @returns {number} Probability of positive class (stroke)
   */
  predictProba(features) {
    if (!this.loaded) {
      console.warn('XGBoost model not loaded');
      return 0;
    }

    // Sum predictions from all trees
    let sum = 0;
    for (const tree of this.trees) {
      sum += this.evaluateTree(tree, features);
    }

    // Apply sigmoid for binary classification
    const probability = 1 / (1 + Math.exp(-sum));
    return probability;
  }

  /**
   * Predict class (0 or 1)
   * @param {Array} features - Feature array
   * @param {number} threshold - Classification threshold (default 0.5)
   * @returns {number} Predicted class (0 or 1)
   */
  predict(features, threshold = 0.5) {
    const prob = this.predictProba(features);
    return prob >= threshold ? 1 : 0;
  }
}

// Constants for feature extraction
export const TEMPORAL_WINDOW = 7;
// Features per frame (16 total) - must match Python pickleball.py:
// - ball_x, ball_y (2) - normalized position (interpolated if missing)
// - paddle_x, paddle_y (2) - normalized position (interpolated if missing)
// - ball_paddle_dist (1) - distance between ball and paddle
// - ball_velocity (1) - ball speed magnitude sqrt(vx² + vy²)
// - ball_acceleration (1) - change in ball velocity magnitude
// - ball_acceleration_x (1) - horizontal component of ball acceleration (dvx)
// - paddle_velocity (1) - paddle speed magnitude
// - paddle_acceleration (1) - change in paddle velocity magnitude
// - paddle_acceleration_x (1) - horizontal component of paddle acceleration (dvx)
// - ball_is_interpolated (1) - 1 if ball was interpolated, 0 otherwise
// - paddle_is_interpolated (1) - 1 if paddle was interpolated
// - ball_vx, ball_vy (2) - raw ball velocity components
// - ball_direction_changed_x (1) - 1 if ball horizontal direction changed, 0 otherwise
const FEATURES_PER_FRAME = 16;

// Window-level features (1 total):
// - peak_ball_acceleration: highest ball acceleration within ±2 frames
const WINDOW_FEATURES = 1;

/**
 * Compute velocity vector { vx, vy, magnitude }
 * Returns null if invalid
 */
function computeVelocityVector(current, prev, frameW, frameH) {
  if (!current || !prev) return null;
  if (!isValidDetection(current) || !isValidDetection(prev)) return null;

  const vx = (current.x - prev.x) / frameW;
  const vy = (current.y - prev.y) / frameH;
  const magnitude = Math.sqrt(vx * vx + vy * vy);
  return { vx, vy, magnitude };
}

/**
 * Compute vector acceleration: { dvx, dvy, magnitude }
 * This captures direction changes, not just speed changes
 * Returns null if invalid inputs
 */
function computeVectorAcceleration(v1, v2) {
  if (!v1 || !v2) return null;
  const dvx = v2.vx - v1.vx;
  const dvy = v2.vy - v1.vy;
  const magnitude = Math.sqrt(dvx * dvx + dvy * dvy);
  return { dvx, dvy, magnitude };
}

/**
 * Find peak ball acceleration within ±2 frames of center
 * Uses vector acceleration to capture direction changes
 */
function findPeakBallAcceleration(frameBuffer, centerIdx, frameW, frameH) {
  // Collect velocity vectors for frames in range [center-3, center+2]
  const velocities = {};

  for (let offset = -3; offset <= 2; offset++) {
    const idx = centerIdx + offset;
    const prevIdx = idx - 1;

    if (idx >= 0 && idx < frameBuffer.length && prevIdx >= 0 && prevIdx < frameBuffer.length) {
      const { ball } = getPrimaryDetections(frameBuffer[idx]);
      const { ball: prevBall } = getPrimaryDetections(frameBuffer[prevIdx]);
      const vel = computeVelocityVector(ball, prevBall, frameW, frameH);
      if (vel) {
        velocities[idx] = vel;
      }
    }
  }

  // Compute accelerations for frames in range [center-2, center+2]
  // acceleration[X] = magnitude(velocity[X] - velocity[X-1])
  let peakAcceleration = -1;

  for (let offset = -2; offset <= 2; offset++) {
    const idx = centerIdx + offset;
    const prevIdx = idx - 1;

    if (velocities[idx] && velocities[prevIdx]) {
      const accel = computeVectorAcceleration(velocities[prevIdx], velocities[idx]);
      if (accel && accel.magnitude > peakAcceleration) {
        peakAcceleration = accel.magnitude;
      }
    }
  }

  return peakAcceleration;
}

/**
 * Extract features from a single frame
 *
 * @param {Object} ball - Current ball detection
 * @param {Object} paddle - Current paddle detection
 * @param {Object} prevBall - Previous ball detection
 * @param {Object} prevPaddle - Previous paddle detection
 * @param {Object} prevPrevBall - Ball detection from 2 frames ago (for direction change)
 * @param {Object} prevPrevPaddle - Paddle detection from 2 frames ago
 * @param {Object} prevBallVelVec - Previous ball velocity vector { vx, vy, magnitude } or null
 * @param {Object} prevPaddleVelVec - Previous paddle velocity vector { vx, vy, magnitude } or null
 * @param {boolean} ballInterpolated - Whether ball was interpolated
 * @param {boolean} paddleInterpolated - Whether paddle was interpolated
 * @param {number} frameW - Frame width
 * @param {number} frameH - Frame height
 * @returns {Object} { features, ballVelVec, paddleVelVec }
 */
function frameToFeatures(ball, paddle, prevBall, prevPaddle, prevPrevBall, prevPrevPaddle, prevBallVelVec, prevPaddleVelVec, ballInterpolated, paddleInterpolated, frameW, frameH) {
  const features = [];
  const frameDiag = Math.sqrt(frameW * frameW + frameH * frameH);

  // Ball position (normalized, -1 if missing even after interpolation)
  if (ball) {
    features.push(ball.x / frameW);
    features.push(ball.y / frameH);
  } else {
    features.push(-1, -1);
  }

  // Paddle position (normalized, -1 if missing even after interpolation)
  if (paddle) {
    features.push(paddle.x / frameW);
    features.push(paddle.y / frameH);
  } else {
    features.push(-1, -1);
  }

  // Ball-paddle distance (normalized, -1 if either missing)
  if (ball && paddle) {
    const dist = Math.sqrt((ball.x - paddle.x) ** 2 + (ball.y - paddle.y) ** 2);
    features.push(dist / frameDiag);
  } else {
    features.push(-1);
  }

  // Ball velocity vector
  let ballVelVec = null;
  let ballVx = -1;
  let ballVy = -1;
  if (ball && prevBall) {
    ballVx = (ball.x - prevBall.x) / frameW;
    ballVy = (ball.y - prevBall.y) / frameH;
    const magnitude = Math.sqrt(ballVx * ballVx + ballVy * ballVy);
    ballVelVec = { vx: ballVx, vy: ballVy, magnitude };
  }
  features.push(ballVelVec ? ballVelVec.magnitude : -1);

  // Ball acceleration (change in velocity magnitude)
  let ballAccelVec = null;
  if (ballVelVec && prevBallVelVec) {
    ballAccelVec = computeVectorAcceleration(prevBallVelVec, ballVelVec);
  }
  features.push(ballAccelVec ? ballAccelVec.magnitude : -1);
  features.push(ballAccelVec ? ballAccelVec.dvx : -1); // ball_acceleration_x

  // Paddle velocity vector
  let paddleVelVec = null;
  if (paddle && prevPaddle) {
    const pvx = (paddle.x - prevPaddle.x) / frameW;
    const pvy = (paddle.y - prevPaddle.y) / frameH;
    const magnitude = Math.sqrt(pvx * pvx + pvy * pvy);
    paddleVelVec = { vx: pvx, vy: pvy, magnitude };
  }
  features.push(paddleVelVec ? paddleVelVec.magnitude : -1);

  // Paddle acceleration (change in velocity magnitude)
  let paddleAccelVec = null;
  if (paddleVelVec && prevPaddleVelVec) {
    paddleAccelVec = computeVectorAcceleration(prevPaddleVelVec, paddleVelVec);
  }
  features.push(paddleAccelVec ? paddleAccelVec.magnitude : -1);
  features.push(paddleAccelVec ? paddleAccelVec.dvx : -1); // paddle_acceleration_x

  // Interpolation flags
  features.push(ballInterpolated ? 1 : 0);
  features.push(paddleInterpolated ? 1 : 0);

  // Raw ball velocity components
  features.push(ballVx);
  features.push(ballVy);

  // Ball horizontal direction changed (sign of vx flipped)
  // 1 if direction changed, 0 otherwise, -1 if can't determine
  // Must match Python: compute prev velocity from prevBall and prevPrevBall positions directly
  let ballDirectionChangedX = -1;
  if (ballVx !== -1 && prevBall && prevPrevBall) {
    const prevBallVxForDir = (prevBall.x - prevPrevBall.x) / frameW;
    // Direction changed if signs are different and both are non-zero
    if (prevBallVxForDir !== 0 && ballVx !== 0) {
      ballDirectionChangedX = (Math.sign(ballVx) !== Math.sign(prevBallVxForDir)) ? 1 : 0;
    } else {
      ballDirectionChangedX = 0; // No direction change if either is zero
    }
  }
  features.push(ballDirectionChangedX);

  return { features, ballVelVec, paddleVelVec };
}

/**
 * Extract temporal features for XGBoost stroke detection.
 *
 * Total features: (2 * TEMPORAL_WINDOW + 1) * 15 + 1 = 226 features for window=7
 * - 15 features per frame × 15 frames = 225 frame features
 * - 1 window-level feature (peak_ball_acceleration)
 *
 * Uses prediction for missing detections (velocity extrapolation from t-1, t-2).
 * Prediction stops after maxAncestorDepth consecutive missing frames.
 *
 * Four modes:
 * 1. Cached (pass cachedPredictions): Uses pre-computed predictions (fastest, O(1))
 * 2. Streaming (pass streamingPredictor): Uses predictor state for frames before buffer
 * 3. Batch/Random access (pass allFrames + bufferStartIdx): Uses recursive cache
 * 4. Standalone (no options): Processes buffer without history
 *
 * @param {Array} frameBuffer - Array of frame objects with ball/paddle detections
 * @param {number} centerIdx - Index of the center frame in the buffer
 * @param {number} frameW - Frame width for normalization (default 640)
 * @param {number} frameH - Frame height for normalization (default 480)
 * @param {Object} options - Optional parameters:
 *   - cachedPredictions: Array of pre-computed predictions parallel to frameBuffer (fastest)
 *   - streamingPredictor: StreamingDetectionPredictor for streaming mode
 *   - allFrames: Full frames array for batch/random access mode
 *   - bufferStartIdx: Start index of buffer in allFrames (for random access)
 *   - maxAncestorDepth: Max prediction chain depth (default: DEFAULT_MAX_ANCESTOR_DEPTH)
 * @returns {Array} Feature array (226 features, always returns, ML handles missing data)
 */
export function extractTemporalFeatures(frameBuffer, centerIdx, frameW = 640, frameH = 480, options = {}) {
  const features = [];
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;

  // Track previous velocity vectors for acceleration calculation
  let prevBallVelVec = null;
  let prevPaddleVelVec = null;

  // Build predictions for all frames we need
  const predictions = {};

  if (options.cachedPredictions) {
    // Cached mode: use pre-computed predictions directly (fastest path, O(1))
    // cachedPredictions is parallel to frameBuffer
    for (let bufferIdx = 0; bufferIdx < options.cachedPredictions.length; bufferIdx++) {
      predictions[bufferIdx] = options.cachedPredictions[bufferIdx];
    }
  } else if (options.allFrames && options.bufferStartIdx !== undefined) {
    // Batch/random access mode: use recursive cache with full frames array
    const cache = {};
    const bufferStartIdx = options.bufferStartIdx;

    // Get predictions for all buffer frames and one before (for velocity)
    // Note: only -1, not -2, to match Python behavior
    for (let bufferIdx = -1; bufferIdx < frameBuffer.length; bufferIdx++) {
      const frameIdx = bufferStartIdx + bufferIdx;
      if (frameIdx >= 0 && frameIdx < options.allFrames.length) {
        const ballResult = getDetectionAtFrame(options.allFrames, frameIdx, 'ball', { maxAncestorDepth, cache });
        const paddleResult = getDetectionAtFrame(options.allFrames, frameIdx, 'paddle', { maxAncestorDepth, cache });
        predictions[bufferIdx] = {
          ball: ballResult.detection,
          paddle: paddleResult.detection,
          ballWasPredicted: ballResult.wasPredicted,
          paddleWasPredicted: paddleResult.wasPredicted
        };
      }
    }
  } else if (options.streamingPredictor) {
    // Streaming mode: use predictor state for frames before buffer
    const predictor = options.streamingPredictor;

    // Get state for frame before buffer (for velocity calculation)
    // Note: Python only sets predictions[-1], not predictions[-2]
    // This means ball_direction_changed_x will be -1 for the first buffer frame
    const prevState = predictor.getPrevState();
    predictions[-1] = {
      ball: prevState.ball,
      paddle: prevState.paddle,
      ballWasPredicted: false,
      paddleWasPredicted: false
    };

    // Process buffer frames sequentially using a local predictor
    const localPredictor = new StreamingDetectionPredictor(maxAncestorDepth);
    if (predictor.prevBall) {
      localPredictor.prevBall = { ...predictor.prevBall };
    }
    if (predictor.prevPrevBall) {
      localPredictor.prevPrevBall = { ...predictor.prevPrevBall };
    }
    if (predictor.prevPaddle) {
      localPredictor.prevPaddle = { ...predictor.prevPaddle };
    }
    if (predictor.prevPrevPaddle) {
      localPredictor.prevPrevPaddle = { ...predictor.prevPrevPaddle };
    }

    for (let bufferIdx = 0; bufferIdx < frameBuffer.length; bufferIdx++) {
      const result = localPredictor.processFrame(frameBuffer[bufferIdx]);
      predictions[bufferIdx] = {
        ball: result.ball,
        paddle: result.paddle,
        ballWasPredicted: result.ballWasPredicted,
        paddleWasPredicted: result.paddleWasPredicted
      };
    }
  } else {
    // Standalone mode: process buffer frames like Python does
    // First, determine the buffer range (same as Python's buffer slicing)
    const bufferStart = Math.max(0, centerIdx - TEMPORAL_WINDOW);
    const bufferEnd = Math.min(frameBuffer.length, centerIdx + TEMPORAL_WINDOW + 1);

    // Process frames before buffer to build streaming state
    const localPredictor = new StreamingDetectionPredictor(maxAncestorDepth);
    for (let i = 0; i < bufferStart; i++) {
      localPredictor.processFrame(frameBuffer[i] || {});
    }

    // Get t-1 state from streaming predictor (matches Python which only sets predictions[-1])
    // Note: predictions[-2] is intentionally NOT set to match Python behavior
    // This means ball_direction_changed_x will be -1 for the first buffer frame
    const prevState = localPredictor.getPrevState();
    predictions[-1] = {
      ball: prevState.ball,
      paddle: prevState.paddle,
      ballWasPredicted: false,
      paddleWasPredicted: false
    };

    // Process buffer frames with buffer-relative indices (0 to buffer_size-1)
    for (let i = bufferStart; i < bufferEnd; i++) {
      const bufferRelativeIdx = i - bufferStart;
      const result = localPredictor.processFrame(frameBuffer[i] || {});
      predictions[bufferRelativeIdx] = {
        ball: result.ball,
        paddle: result.paddle,
        ballWasPredicted: result.ballWasPredicted,
        paddleWasPredicted: result.paddleWasPredicted
      };
    }

    // Adjust centerIdx to buffer-relative index for feature extraction
    // Store original for bounds checking, use buffer-relative for predictions
    options._bufferStart = bufferStart;
    options._useBufferRelativeIdx = true;
  }

  // Extract features for each frame in the window
  for (let offset = -TEMPORAL_WINDOW; offset <= TEMPORAL_WINDOW; offset++) {
    const idx = centerIdx + offset;

    // For standalone mode, use buffer-relative indices for predictions
    // (matches Python's buffer slicing behavior)
    let predIdx, prevPredIdx, prevPrevPredIdx;
    if (options._useBufferRelativeIdx) {
      const bufferStart = options._bufferStart;
      predIdx = idx - bufferStart;
      prevPredIdx = predIdx - 1;
      prevPrevPredIdx = predIdx - 2;
    } else {
      predIdx = idx;
      prevPredIdx = idx - 1;
      prevPrevPredIdx = idx - 2;
    }

    if (idx >= 0 && idx < frameBuffer.length) {
      const pred = predictions[predIdx] || {};
      const prevPred = predictions[prevPredIdx] || {};
      const prevPrevPred = predictions[prevPrevPredIdx] || {};

      const ball = pred.ball || null;
      const paddle = pred.paddle || null;
      const prevBall = prevPred.ball || null;
      const prevPaddle = prevPred.paddle || null;
      const prevPrevBall = prevPrevPred.ball || null;
      const prevPrevPaddle = prevPrevPred.paddle || null;
      const ballInterpolated = pred.ballWasPredicted || false;
      const paddleInterpolated = pred.paddleWasPredicted || false;

      const result = frameToFeatures(
        ball, paddle, prevBall, prevPaddle,
        prevPrevBall, prevPrevPaddle,
        prevBallVelVec, prevPaddleVelVec,
        ballInterpolated, paddleInterpolated,
        frameW, frameH
      );

      features.push(...result.features);
      prevBallVelVec = result.ballVelVec;
      prevPaddleVelVec = result.paddleVelVec;
    } else {
      // Out of bounds - use -1 as sentinel for all features
      for (let i = 0; i < FEATURES_PER_FRAME; i++) {
        features.push(-1);
      }
      prevBallVelVec = null;
      prevPaddleVelVec = null;
    }
  }

  // Add window-level feature: peak ball acceleration within ±2 frames of center
  const peakAccel = findPeakBallAcceleration(frameBuffer, centerIdx, frameW, frameH);
  features.push(peakAccel);

  return features;
}

// Singleton instance for shared use
let _predictor = null;

/**
 * Get or create the shared XGBoost predictor instance
 * @param {string} modelUrl - URL to the model (only used on first call)
 * @returns {Promise<XGBoostPredictor>}
 */
export async function getXGBoostPredictor(modelUrl = '/pickleball-model/xgboost-stroke-model.json') {
  if (!_predictor) {
    _predictor = new XGBoostPredictor();
    await _predictor.load(modelUrl);
  }
  return _predictor;
}

// Re-export DetectionPredictor utilities for convenience
export { StreamingDetectionPredictor, getDetectionAtFrame, buildPredictionsForRange, DEFAULT_MAX_ANCESTOR_DEPTH };
